import {Col, Row} from 'antd';
import {readableColor} from 'polished';
import React, {CSSProperties, FC} from 'react';
import {Redirect, useLocation} from 'react-router-dom';
import {
  Option,
  TBoolean,
  UndefinedTuple,
  useCurrentCategory,
  useCurrentEars,
  useCurrentProduct,
  useCurrentSelectedOptions,
} from '../../contexts/Quote';
import {
  brandLabels,
  COLOR,
  colorHex,
  colorLabels,
  CommonOptionDataKeys,
  CORD_COLOR,
  cordColorHex,
  cordColorLabels,
  DEPORTE_BRAND,
  EAR_BUD,
  earBudLabels,
  EARWAX_SHIELDING,
  earwaxShieldingLabels,
  EVENT,
  eventLabels,
  FILTER,
  filterLabels,
  getTypeLabel,
  isOptionData,
  Material,
  materialLabels,
  MISCELLANEOUS,
  miscellaneousLabels,
  OPTION,
  OPTION_TUBE,
  OptionDataContainer,
  optionLabels,
  optionsTree,
  optionTubeLabels,
  Product,
  ProductCategoryType,
  PROTECTION_SHAPE,
  Shape,
  shapeLabels,
  SHOULDER,
  shoulderLabels,
  SURFACE,
  surfaceLabels,
  TUBE,
  tubeLabels,
  TYPE,
  BOOLEAN_VALUE,
} from './data';
import EarsInput from './EarsInput';
import FormButtons from './FormButtons';
import {OptionsGroupsRows} from './OptionsGroupsRows';
import {Group, OPTION_GROUP_CHECK_TYPE, SelectableOption} from './static';

const deepTypes = (
  types: {name: TYPE; required: boolean; unique: boolean}[],
  data: ProductCategoryType,
): void => {
  if (!data.type) {
    const opt = data as OptionDataContainer;
    if (opt.options) {
      opt.options.map((option): void => {
        const optionType = types.find((type) => type.name === option.type);
        if (!optionType) {
          types.push({
            name: option.type,
            required: option.required || false,
            unique: option.unique || false,
          });
        }

        return undefined;
      });
    }
    return;
  }

  const keys = Object.keys(data).filter(
    (key) => !CommonOptionDataKeys.includes(key),
  );

  const optionType = types.find((type) => type.name === data.type);
  if (!optionType) {
    const {required = false, unique = false} = isOptionData(data)
      ? data
      : {required: true, unique: true};

    types.push({
      name: data.type as TYPE,
      required,
      unique,
    });
  }

  keys.map((key): void => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
    // @ts-ignore
    deepTypes(types, data[key]);

    return undefined;
  });
};

export const getOptionLabel = (type: TYPE, name: string): string => {
  switch (type) {
    case TYPE.BRAND:
      return brandLabels[name as DEPORTE_BRAND];
    case TYPE.SHAPE:
      return shapeLabels[name as Shape];
    case TYPE.MATERIAL:
      return materialLabels[name as Material];
    case TYPE.COLOR:
      return colorLabels[name as COLOR];
    case TYPE.FILTER:
      return filterLabels[name as FILTER];
    case TYPE.CORD_COLOR:
      return cordColorLabels[name as CORD_COLOR];
    case TYPE.SURFACE:
      return surfaceLabels[name as SURFACE];
    case TYPE.EVENT:
      return eventLabels[name as EVENT];
    case TYPE.TUBE:
      return tubeLabels[name as TUBE];
    case TYPE.OPTION_TUBE:
      return optionTubeLabels[name as OPTION_TUBE];
    case TYPE.SHOULDER:
      return shoulderLabels[name as SHOULDER];
    case TYPE.EAR_BUD:
      return earBudLabels[name as EAR_BUD];
    case TYPE.MISCELLANEOUS:
      return miscellaneousLabels[name as MISCELLANEOUS];
    case TYPE.EARWAX_SHIELDING:
      return earwaxShieldingLabels[name as EARWAX_SHIELDING];
    case TYPE.OPTION:
      return optionLabels[name as OPTION];
    case TYPE.COLOR_MARKER:
    case TYPE.OTOSCAN:
    case TYPE.EAR_BUD_ASSEMBLY:
      return getTypeLabel(type);
    case TYPE.FLOAT:
      return 'Flotteur';
    case TYPE.ENGRAVING:
      return 'Gravure Prénom + Nom';
    case TYPE.CAMO:
      return 'Camouflage';
    case TYPE.CORD:
      return 'Cordon';
    default:
      return '%MISSING_LABEL%';
  }
};

const getOptionStyle = (
  type: TYPE,
  name: string,
): CSSProperties | undefined => {
  const color = ((): string => {
    switch (type) {
      case TYPE.COLOR:
        return colorHex[name as COLOR];
      case TYPE.CORD_COLOR:
        return cordColorHex[name as CORD_COLOR];
      default:
        return '';
    }
  })();

  return color
    ? {backgroundColor: color, color: readableColor(color)}
    : undefined;
};

const deepOptions = (
  options: SelectableOption[],
  data: ProductCategoryType,
  type: TYPE,
  ears: TBoolean,
  selectedOptions: Option[],
  parentSelected: TBoolean,
): void => {
  if (!data.type) {
    const opt = data as OptionDataContainer;
    if (opt.options) {
      opt.options.map((option): void => {
        if (option.type !== type) {
          return undefined;
        }

        option.values.map((optionName: string): void => {
          let existingOption = options.find(
            ({type: optionType, name}) =>
              type === optionType && name === optionName,
          );
          if (!existingOption) {
            existingOption = {
              type: option.type,
              label: getOptionLabel(option.type, optionName),
              style: getOptionStyle(option.type, optionName),
              name: optionName,
              disabled: [
                !ears[0] || !parentSelected[0],
                !ears[1] || !parentSelected[1],
              ],
              checked: [false, false],
            };

            options.push(existingOption);
          } else {
            if (existingOption.disabled[0]) {
              existingOption.disabled[0] = !ears[0] || !parentSelected[0];
            }
            if (existingOption.disabled[1]) {
              existingOption.disabled[1] = !ears[1] || !parentSelected[1];
            }
          }

          let selected: TBoolean = [false, false];
          let value: UndefinedTuple<number> = [undefined, undefined];
          const selectedOption = selectedOptions.find(
            ({type: optionType, name}) =>
              type === optionType && name === optionName,
          );
          if (selectedOption) {
            [selected, value] = [
              selectedOption.selected,
              selectedOption.value || value,
            ];
          }

          existingOption.checked[0] =
            !existingOption.disabled[0] && selected[0];
          existingOption.checked[1] =
            !existingOption.disabled[1] && selected[1];

          existingOption.value = existingOption.value || [undefined, undefined];
          existingOption.value[0] = !existingOption.disabled[0]
            ? value[0]
            : undefined;
          existingOption.value[1] = !existingOption.disabled[1]
            ? value[1]
            : undefined;

          return undefined;
        });
        return undefined;
      });
    }

    return;
  }

  const keys = Object.keys(data).filter(
    (key) => !CommonOptionDataKeys.includes(key),
  );

  if (data.type === type) {
    keys.map((optionName: string): void => {
      const optName = optionName === 'values' ? BOOLEAN_VALUE : optionName;
      let existingOption = options.find(
        ({type: optionType, name}) => type === optionType && name === optName,
      );
      if (!existingOption) {
        existingOption = {
          type: data.type as TYPE,
          label: getOptionLabel(data.type as TYPE, optName),
          style: getOptionStyle(data.type as TYPE, optName),
          name: optName,
          disabled: [
            !ears[0] || !parentSelected[0],
            !ears[1] || !parentSelected[1],
          ],
          checked: [false, false],
        };

        options.push(existingOption);
      } else {
        if (existingOption.disabled[0]) {
          existingOption.disabled[0] = !ears[0] || !parentSelected[0];
        }
        if (existingOption.disabled[1]) {
          existingOption.disabled[1] = !ears[1] || !parentSelected[1];
        }
      }

      let selected: TBoolean = [false, false];
      let value: UndefinedTuple<number> = [undefined, undefined];
      const selectedOption = selectedOptions.find(
        ({type: optionType, name}) => type === optionType && name === optName,
      );
      if (selectedOption) {
        [selected, value] = [
          selectedOption.selected,
          selectedOption.value || value,
        ];
      }

      existingOption.checked[0] = !existingOption.disabled[0] && selected[0];
      existingOption.checked[1] = !existingOption.disabled[1] && selected[1];

      existingOption.value = existingOption.value || [undefined, undefined];
      existingOption.value[0] = !existingOption.disabled[0]
        ? value[0]
        : undefined;
      existingOption.value[1] = !existingOption.disabled[1]
        ? value[1]
        : undefined;

      return undefined;
    });
  }

  keys.map((key): void => {
    const parentSelected = selectedOptions.find(({name}) => name === key); // TODO store parent type in children?
    const selected: TBoolean = (parentSelected && parentSelected.selected) || [
      false,
      false,
    ];

    deepOptions(
      options,
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      data[key],
      type,
      ears,
      selectedOptions,
      selected,
    );

    return undefined;
  });
};

const setSelectedOption = (
  option: Option,
  checkType: OPTION_GROUP_CHECK_TYPE,
  selectedOptions: Option[],
  setSelectedOptions: (options: Option[]) => void,
): void => {
  const removedIndexes: number[] = [];
  const remove =
    option.selected.filter((selected) => selected === true).length === 0;

  const index = selectedOptions.findIndex(
    ({type, name}) => type === option.type && name === option.name,
  );
  if (index !== -1) {
    if (remove) {
      removedIndexes.push(index);
    } else {
      selectedOptions[index].selected = option.selected;
      selectedOptions[index].value = option.value;
    }
  } else if (!remove) {
    selectedOptions.push(option);
  }

  if (
    [
      OPTION_GROUP_CHECK_TYPE.RADIO,
      OPTION_GROUP_CHECK_TYPE.EVENT_INPUT,
    ].includes(checkType)
  ) {
    const otherOptions = selectedOptions.filter(
      ({name, type}) => type === option.type && name !== option.name,
    );
    otherOptions.map((otherOption): void => {
      if (otherOption.selected[0] && option.selected[0]) {
        otherOption.selected[0] = false;
        otherOption.value && (otherOption.value[0] = undefined);
      }
      if (otherOption.selected[1] && option.selected[1]) {
        otherOption.selected[1] = false;
        otherOption.value && (otherOption.value[1] = undefined);
      }

      if (
        otherOption.selected.filter((selected) => selected === true).length ===
        0
      ) {
        removedIndexes.push(selectedOptions.indexOf(otherOption));
      }

      return undefined;
    });
  }

  setSelectedOptions(
    selectedOptions.filter((_, index) => !removedIndexes.includes(index)),
  );

  return undefined;
};

export const OptionsGroups: FC<{parentPath: string}> = ({parentPath}) => {
  const [category] = useCurrentCategory();
  const [product] = useCurrentProduct();
  const [ears] = useCurrentEars();
  const [selectedOptions, setSelectedOptions] = useCurrentSelectedOptions();

  const {pathname} = useLocation();
  const pathTokens = pathname.split('/');

  const groups: Group[] = [];
  const options: SelectableOption[] = [];

  let selectedGroup: Group | undefined;
  if (category && product) {
    const selectedProduct = optionsTree[category.name][product.name];
    if (!selectedProduct) {
      return null;
    }

    const types: {name: TYPE; required: boolean; unique: boolean}[] = [];
    deepTypes(types, selectedProduct);
    groups.push(
      ...types.map((type) => ({
        ...type,
        label: getTypeLabel(type.name),
        disabled: true,
        checkType:
          type.name === TYPE.EVENT
            ? OPTION_GROUP_CHECK_TYPE.EVENT_INPUT
            : undefined,
      })),
    );

    selectedGroup = groups.find(
      ({name}) => name === pathTokens[pathTokens.length - 1],
    );

    if (!selectedGroup && groups.length > 0) {
      return <Redirect to={['options', groups[0].name].join('/')} />;
    }

    if (selectedGroup) {
      deepOptions(
        options,
        selectedProduct,
        selectedGroup.name as TYPE,
        ears,
        selectedOptions,
        ears,
      );
      let change = false;
      const removedIndexes: number[] = [];
      selectedOptions.map((option, index): void => {
        const typeOptions: SelectableOption[] = [];
        deepOptions(
          typeOptions,
          selectedProduct,
          option.type,
          ears,
          selectedOptions,
          [true, true],
        );

        const opt = typeOptions.find(({name}) => name === option.name);
        if (!opt) {
          return undefined;
        }

        if (option.selected.toString() !== opt.checked.toString()) {
          change = true;
          option.selected = [...opt.checked] as TBoolean;

          if (
            option.selected.filter((selected) => selected === true).length === 0
          ) {
            removedIndexes.push(index);
          }
        }

        return undefined;
      });
      if (change) {
        setSelectedOptions(
          selectedOptions.filter((_, index) => !removedIndexes.includes(index)),
        );
      }

      groups.map((group): void => {
        const groupOptions: SelectableOption[] = [];

        deepOptions(
          groupOptions,
          // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
          // @ts-ignore
          selectedProduct,
          group.name as TYPE,
          ears,
          selectedOptions,
          ears,
        );

        group.disabled =
          groupOptions.filter(
            (option) =>
              option.disabled.filter((disabled) => disabled === false).length >
              0,
          ).length === 0;

        return undefined;
      });
    }
  }

  const onOptionSelect = (
    option: Option,
    checkType: OPTION_GROUP_CHECK_TYPE,
  ): void => {
    setSelectedOption(option, checkType, selectedOptions, setSelectedOptions);
  };

  return (
    <Row>
      <Col span={24}>
        <FormButtons groups={groups} parentPath={parentPath}>
          <EarsInput />
        </FormButtons>
        {product?.name === PROTECTION_SHAPE.EAR ? (
          <p css={{textAlign: 'center', color: 'red', fontWeight: 'bold'}}>
            Envoi des écouteurs obligatoire pour adaptation
          </p>
        ) : null}
        <OptionsGroupsRows
          ears={ears}
          groups={groups}
          options={options}
          selectedGroup={selectedGroup}
          setSelectedOption={onOptionSelect}
        />
        {groups.length ? <FormButtons groups={groups} parentPath={parentPath} /> : null}
      </Col>
    </Row>
  );
};
